import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, delay, first, map, Observable, skip, Subject, switchMap, takeUntil, takeWhile, tap } from 'rxjs';
import { LgvAPIService, LgvClientService, LgvConstants, LgvUserService, NotificationService } from '../../core/core.module';
import { ObjectValidator } from '../../core/utility/lgv-form-validator';
import { RegexConstants, RegexValidator } from '../../core/utility/lgv-regex-validator';
import { LgvModel } from '../../data-models/longevity.model';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { environment as env } from '../../../environments/environment';
import { LocationService } from '../../core/services/location.service';

@Component({
    selector: 'lus-user-edit-modal',
    templateUrl: './user-edit-modal.component.html',
    styleUrls: ['./user-edit-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserEditModalComponent implements OnInit {


    @Output() OnOKClicked: EventEmitter<string> = new EventEmitter();

    isLoading$ = new BehaviorSubject(false);
    isLoadingOrganization$ = new BehaviorSubject(false);
    userInfoForm: UntypedFormGroup;
    onDestroy$ = new Subject();

    //userInfo: LgvModel.Agent;

    companyIdParam: string = null;
    userInfoParam: LgvModel.Agent = null;

    _filteredOrganization: LgvModel.Organization[] = [];
    filteredOrganization$ = new BehaviorSubject(this._filteredOrganization);

    selectedOrganization: LgvModel.Organization;

    rolesToShow = ['Agent'];
    additionalRolesForAdmins = ['Admin', 'Assistant'];
    get filteredRoles$(): Observable<LgvModel.Role[]> {
        return this.lgvUserSvc.roles$.pipe(map(arr => {
            const rolesToShow = this.rolesToShow;
            if (this.lgvUserSvc.IsCompanyAdmin) {
                rolesToShow.push('CompanyAdmin');
            }
            const filteredByRolesToShow = arr.filter(x => rolesToShow.includes(x.code) || (this.lgvApiSvc.IsAdmin && this.additionalRolesForAdmins.includes(x.code)))
            return filteredByRolesToShow;
        }))
    }

    get isCreateMode(): boolean { return !(!!this.userInfoParam); }
    get title(): string { return this.isCreateMode ? 'New User' : 'Edit User'; }

    constructor(
        private http: HttpClient,
        private fb: UntypedFormBuilder,
        public lgvClientSvc: LgvClientService,
        public notificationSvc: NotificationService,
        public lgvApiSvc: LgvAPIService,
        public lgvUserSvc: LgvUserService,
        private route: ActivatedRoute,
        public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
        public MatDialog: MatDialog,
        private locationService: LocationService,
        @Inject(MAT_DIALOG_DATA) public data: any) {

        if (!!data?.companyId) {
            this.companyIdParam = data.companyId;
        }

        if (!!data?.userInfo) {
            this.userInfoParam = data.userInfo;
        }
    }


    ngOnInit(): void {
        this.lgvUserSvc.LoadUserRoles();
        this.initializeForm();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    displayOrg(organization: LgvModel.Organization) {
        if (organization) { return `${organization.name}`; }
    }

    onOrganizationSelected(org: LgvModel.Organization) {
        this.selectedOrganization = org;
    }

    initializeForm() {

        this.userInfoForm = this.fb.group({
            firstName: [null, [Validators.required]],
            lastName: [null, [Validators.required]],
            email: [null, [Validators.required, Validators.email]],
            companyId: [this.companyIdParam, [Validators.required]],
            organizationId: [null, [Validators.required]],
            roles: [null, [Validators.required]],
            address1: [null],
            city: [null],
            state: [null],
            zipCode: [null],
            profileImageUrl: [null],
            phone: [null],
            mobilePhone: [null],
            fax: [null],
            jobTitle: [null],
            nmlsid: [null],
            licenseNumber: [null],
            website: [null],
            applicationUrl: [null],
            obchannelIndex: [null],
            oboriginatorIndex: [null]
        });

        if (!this.isCreateMode) {
            this.userInfoForm.controls['jobTitle'].addValidators([Validators.required]);
            this.userInfoForm.patchValue(this.userInfoParam);

            this.lgvUserSvc.AgentRoles$.pipe(first(f => f !== null), takeUntil(this.onDestroy$)).subscribe(x => {
                this.userInfoForm.controls.roles.setValue(x);
            });

        }

        if (this.data.isFromOnboarding && this.data?.isREAgent) {
            this.userInfoForm.controls['licenseNumber'].addValidators([Validators.required]);
        }

        if (this.data.isFromOnboarding && !this.data?.isREAgent) {
            this.userInfoForm.controls['nmlsid'].addValidators([Validators.required]);
        }

        this.userInfoForm
            .get('organizationId')
            .valueChanges
            .pipe(
                skip(1),
                tap(() => this.isLoadingOrganization$.next(true)),
                switchMap(txtSearch => this.lgvApiSvc.SearchWebServiceOrganization(txtSearch, this.userInfoForm.controls.companyId.value)
                ),
                delay(600),
                tap(() => {
                    this.isLoadingOrganization$.next(false);
                }),
                takeUntil(this.onDestroy$)
            ).subscribe(x => {
                this.filteredOrganization$.next(x.value);
            });

            this.userInfoForm
            .get('zipCode')
            .valueChanges
            .pipe(takeUntil(this.onDestroy$)).subscribe((zip)=> {
                this.lookUpLocationInfoFromZip(zip);
            });
    }


    lookUpLocationInfoFromZip(zipCode: string) {
        // Get userId from the user being edited (if editing), or the current user
        const userId = this.userInfoParam?.id || this.lgvUserSvc?.DBUserSS$?.value?.agentInfo?.id;

        this.locationService.lookupLocationByZip(zipCode, userId)
            .subscribe(
                locationInfo => {
                    if (locationInfo) {
                        this.userInfoForm.get('state').setValue(locationInfo.stateCode);
                        this.userInfoForm.get('city').setValue(locationInfo.city);
                    } else {
                        // Handle no locations returned (user not licensed in this state)
                        this.notificationSvc.error(`No licensed locations found for zipcode ${zipCode}. Please verify that licenses are set up for this state.`);
                        // Clear state and city fields
                        this.userInfoForm.get('state').setValue(null);
                        this.userInfoForm.get('city').setValue(null);
                    }
                },
                (err) => {
                    this.notificationSvc.error(`Unable to get location information for ${zipCode}`);
                    console.log('Error looking up zipcode info');
                    console.log(err);
                    // Clear state and city fields
                    this.userInfoForm.get('state').setValue(null);
                    this.userInfoForm.get('city').setValue(null);
                }
            );
    }


    onCancel(): void {
        this.dialogRef.close({ action: false });
    }


    onSave() {
        if (this.userInfoForm.invalid) { return; }

        let model = this.userInfoForm.getRawValue();


        if (this.isCreateMode) {
            model.organizationId = this.selectedOrganization.id;
            this.lgvUserSvc.CreateUser(model).pipe(takeUntil(this.onDestroy$)).subscribe(x => {
                this.notificationSvc.success('User has been created');
                this.dialogRef.close({ action: true });
            },
                err => {

                    let isEmailAlreadyExists = false;

                    if (err instanceof HttpErrorResponse) {
                        let httpErrror = err as HttpErrorResponse;

                        if (!!httpErrror?.error?.errors?.Email[0]) {
                            if ((httpErrror?.error?.errors?.Email[0] as string).includes('already been used')) {
                                isEmailAlreadyExists = true;
                            }
                        }
                    }

                    if (isEmailAlreadyExists) {
                        this.notificationSvc.error("This email is already used.");
                    }
                    else {
                        this.notificationSvc.error("There was an error saving this user");
                    }

                });

        } else {
            model.companyId = this.userInfoParam.companyId;
            model.organizationId = this.userInfoParam.organizationId;

            let bldUrl = env.LgvApiBaseUrl;

            bldUrl += `/users/${this.userInfoParam.id}`;

            this.lgvUserSvc

            this.http.patch<any>(bldUrl, model).pipe(takeUntil(this.onDestroy$)).subscribe(x => {
                this.notificationSvc.success('Profile has been updated');
                this.dialogRef.close({ action: true });
            },
                err => {
                    console.log(err);
                    this.notificationSvc.error("There was an error saving this user");
                });
        }

    }
}
